import type { TranslationKey } from "@privacy/translations";
import Typography from "@mui/material/Typography";
import { useTranslation } from "@privacy/translations";
import type { FC } from "react";
import { Box, Stack } from "@mui/material";
import type { Role } from "../../api/users.types";
import { HoverPopover, useHoverPopover } from "../../shared/HoverPopover";
import { InfoIcon } from "../../shared/icons";
import { useCurrentOrganisation } from "../../root/CurrentOrganisationProvider";

export const DISPLAYED_ROLES = [
  "PRIVACY_ONE_READ_ONLY",
  "PRIVACY_ONE_CREATOR",
  "PRIVACY_ONE_INVITOR",
  "PRIVACY_ONE_REVIEWER",
  "SYSTEM_OWNER",
  "PRODUCT_ADMIN",
] satisfies Role[];

export type DisplayedRole = (typeof DISPLAYED_ROLES)[number];

export const ROLES_CONTROLLED_BY_ROOT: Role[] = ["SYSTEM_OWNER", "PRODUCT_ADMIN"];

export const roleTexts: Record<DisplayedRole, { labelKey: TranslationKey; infoKey: TranslationKey }> = {
  PRIVACY_ONE_READ_ONLY: { labelKey: "Roles.PRIVACY_ONE_READ_ONLY", infoKey: "Roles.PRIVACY_ONE_READ_ONLY.Info" },
  PRIVACY_ONE_CREATOR: { labelKey: "Roles.PRIVACY_ONE_CREATOR", infoKey: "Roles.PRIVACY_ONE_CREATOR.Info" },
  PRIVACY_ONE_INVITOR: { labelKey: "Roles.PRIVACY_ONE_INVITOR", infoKey: "Roles.PRIVACY_ONE_INVITOR.Info" },
  PRIVACY_ONE_REVIEWER: { labelKey: "Roles.PRIVACY_ONE_REVIEWER", infoKey: "Roles.PRIVACY_ONE_REVIEWER.Info" },
  SYSTEM_OWNER: { labelKey: "Roles.SYSTEM_OWNER", infoKey: "Roles.SYSTEM_OWNER.Info" },
  PRODUCT_ADMIN: { labelKey: "Roles.PRODUCT_ADMIN", infoKey: "Roles.PRODUCT_ADMIN.Info" },
};

/** With SCIM enabled, persons and roles come from the SCIM mapping, so Product Admin gets a different description */
export const useRoleInfoKey = () => {
  const { scimEnabled } = useCurrentOrganisation();
  return (role: DisplayedRole): TranslationKey =>
    role === "PRODUCT_ADMIN" && scimEnabled ? "Roles.PRODUCT_ADMIN.Info.Scim" : roleTexts[role].infoKey;
};

type RolesInfoPopoverProps = {
  popoverProps: HoverPopover["popoverProps"];
  role: DisplayedRole;
};
const RolesInfoPopover = ({ popoverProps, role }: RolesInfoPopoverProps) => {
  const { t } = useTranslation();
  const roleInfoKey = useRoleInfoKey();
  return (
    <HoverPopover {...popoverProps}>
      <Box sx={{ py: 2, px: 3, maxWidth: "16rem" }}>
        <Typography variant="body2" sx={{ fontSize: theme => theme.typography.pxToRem(12), textWrap: "pretty" }}>
          {t(roleInfoKey(role))}
        </Typography>
      </Box>
    </HoverPopover>
  );
};

export const RoleGridColumnHeader: FC<{ role: DisplayedRole }> = ({ role }) => {
  const { t } = useTranslation();
  const { popoverProps, anchorProps } = useHoverPopover();
  return (
    <>
      <Stack
        direction="row"
        {...anchorProps}
        sx={{
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(12), fontWeight: "600" }}>
          {t(roleTexts[role].labelKey)}
        </Typography>
        <InfoIcon strokeWidth={2.5} size={10} color={theme => theme.palette.primary.main} />
      </Stack>
      <RolesInfoPopover popoverProps={popoverProps} role={role} />
    </>
  );
};

/** number of  characters * average character width + info icon width + safe extra space */
export const getRoleGridColumnWidth = (roleLabel: string) => roleLabel.length * 8 + 40;
